import { inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { RequestEntitiesService } from './request-entities.service';
import { RequestsApiService } from './requests-api.service';
import {
  Counterparty,
  DenyActTypeEnum,
  MainRequestFilesPayload,
  RequestDTO,
  RequestFile,
  RequestFileCategory,
  RequestFileCategoryGroup,
  TemplatesOfActsPayload,
} from '@core/types';
import { map, Observable, Subject } from 'rxjs';
import { format } from 'date-fns';
import { environment } from '@environment/environment';

import { FileGroupsWithFilesToSend } from '@features/requests/request-form/components/request-form-files/types';

@Injectable({
  providedIn: 'root',
})
export class RequestFilesService extends RequestEntitiesService<RequestFile[]> {
  private readonly requestsApiService = inject(RequestsApiService);
  private readonly httpClient = inject(HttpClient);

  readonly source$ = (requestId: number) => {
    return this.requestsApiService.getRequestFiles$(requestId);
  };

  private readonly _$loadedRequestsMainFiles = signal<MainRequestFilesPayload>(
    {},
  );

  get loadedRequestsMainFiles() {
    return this._$loadedRequestsMainFiles.asReadonly();
  }

  public readonly mainRequestFilesRenamePrefixes = [
    'obrz_pts_psm',
    'photo_pl',
    'obrz_app_k_dl',
    'obrz_app_k_dkp',
  ];

  public readonly requestsMainFilesWereUpdated$ = new Subject<void>();

  public setLoadedRequestsMainFiles(mainFiles: MainRequestFilesPayload) {
    this._$loadedRequestsMainFiles.set({ ...mainFiles });
  }

  getRequestFileCategories$(counterpartyIds: Counterparty['id'][]) {
    return this.requestsApiService.getRequestFileCategories$(counterpartyIds);
  }

  getRequestFileCategoryGroups$(counterpartyIds: Counterparty['id'][]) {
    return this.requestsApiService.getRequestFileCategoryGroups$(
      counterpartyIds,
    );
  }

  loadRequestsMainFile$(requestsIds: RequestDTO['id'][]) {
    return this.requestsApiService.getRequestsMainFiles(requestsIds);
  }

  downloadDenyAct$(
    requestId: RequestDTO['id'],
    actType: DenyActTypeEnum,
  ): Observable<{ blob: Blob; filename: string }> {
    return this.requestsApiService.downloadDenyAct$(requestId, actType).pipe(
      map(arrayBuf => {
        const isCarReview = actType === DenyActTypeEnum.CAR_REVIEW;
        const mimeType = isCarReview
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
        const extension = isCarReview ? 'xlsx' : 'docx';
        const blob = new Blob([arrayBuf], { type: mimeType });
        // Двоеточие в имени файла ломает сохранение на Windows и iOS — используем дефис
        const filename = `File_${actType}_${format(new Date(), 'yyyy-MM-dd_HH-mm')}.${extension}`;

        return { blob, filename };
      }),
    );
  }

  uploadRequestFile$(
    requestId: RequestDTO['id'],
    requestNumber: RequestDTO['requestNumber'],
    fileCat: RequestFileCategory,
    file: File,
  ) {
    return this.requestsApiService.uploadRequestFile$(
      requestId,
      requestNumber,
      fileCat,
      file,
    );
  }

  saveRequestFileName$(
    requestId: RequestDTO['id'],
    catId: RequestFileCategory['id'],
    filename: string,
    originFilename: string,
  ) {
    return this.requestsApiService.saveRequestFileName$(
      requestId,
      catId,
      filename,
      originFilename,
    );
  }

  deleteRequestFile$(file: RequestFile) {
    return this.requestsApiService.deleteRequestFile$(file);
  }

  createFileDeleteRequest$(requestId: RequestDTO['id'], file: RequestFile) {
    return this.requestsApiService.createFileDeleteRequest$(requestId, file);
  }

  getFileCatGroupDownloadLink(
    requestId: RequestDTO['id'],
    categoryGroupId: RequestFileCategoryGroup['id'],
  ) {
    return this.requestsApiService.getFileCatGroupDownloadLink(
      requestId,
      categoryGroupId,
    );
  }

  loadTemplatesOfActs$(
    requestNumber: RequestDTO['requestNumber'],
  ): Observable<TemplatesOfActsPayload> {
    return this.requestsApiService.loadTemplatesOfActs$(requestNumber);
  }

  openRequestPdf$(requestId: RequestDTO['id']) {
    return this.requestsApiService.openRequestPdf$(requestId);
  }

  getFileBlobByPath$(path: string): Observable<Blob> {
    return this.httpClient.get(`${environment.downloadFilesUrl}${path}`, {
      responseType: 'blob',
    });
  }

  generateAct$(requestNumber: RequestDTO['requestNumber'], templateId: number) {
    return this.requestsApiService.generateAct$(requestNumber, templateId);
  }

  generateAllActs$(requestNumber: RequestDTO['requestNumber']) {
    return this.requestsApiService.generateAllActs$(requestNumber);
  }

  sendFilesByEmail$(
    requestId: RequestDTO['id'],
    email: string,
    fileGroupsWithFiles: FileGroupsWithFilesToSend,
  ) {
    return this.requestsApiService.sendFilesByEmail$(
      requestId,
      email,
      fileGroupsWithFiles,
    );
  }
}
